'use client';
import dynamic from "next/dynamic";

import "./text_editor_input.css";
//import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import ReactDOMServer from 'react-dom/server';

import CustomSVG from "../svg";
import classes from "./text_input.module.css";
import SvgError from '@/assets/svg/icons/alert-circle.svg';
import Bold from '@/assets/svg/icons/bold.svg';
import Italic from '@/assets/svg/icons/italic.svg';
import Underline from '@/assets/svg/icons/underline.svg';
import Bullet from '@/assets/svg/icons/list.svg';
import Ordered from '@/assets/svg/ordered-list.svg';
import { forwardRef, Fragment, useMemo, useRef, useState } from "react";
import CustomButton from "../buttons/button";
import { Dropdown } from "react-bootstrap";
import MoreHorizontal from '@/assets/svg/icons/more-horizontal.svg';

const ReactQuill = dynamic(
    async () => {
        const { default: RQ } = await import("react-quill");

        const icons = RQ.Quill.import('ui/icons');

        // Register placeholder highlight blot (inline token with yellow bg + tooltip)
        if (!RQ.Quill.imports['formats/placeholder-token']) {
            const Inline = RQ.Quill.import('blots/inline');
            class PlaceholderBlot extends Inline {
                static create(value) {
                    const node = super.create();
                    if (value && typeof value === 'string') {
                        node.setAttribute('title', value);
                    }
                    return node;
                }
                static formats(node) {
                    return node.getAttribute('title') || true;
                }
            }
            PlaceholderBlot.blotName = 'placeholder-token';
            PlaceholderBlot.tagName = 'span';
            PlaceholderBlot.className = 'ql-placeholder-token';
            RQ.Quill.register(PlaceholderBlot, true);
        }

        icons["bold"] = <CustomSVG SvgFunction={Bold} className={`stroke-gray-500 rect-stroke-gray-500 circle-stroke-gray-500`} height={20} width={20}/>;
        icons["italic"] = <CustomSVG SvgFunction={Italic} className={`stroke-gray-500 rect-stroke-gray-500 circle-stroke-gray-500`} height={20} width={20}/>;
        icons["underline"] = <CustomSVG SvgFunction={Underline} className={`stroke-gray-500 rect-stroke-gray-500 circle-stroke-gray-500`} height={20} width={20}/>;
        icons["list"] = {
            ordered: ReactDOMServer.renderToString(<CustomSVG SvgFunction={Ordered} className={`stroke-gray-500 rect-stroke-gray-500 circle-stroke-gray-500`} height={20} width={20}/>),
            bullet: ReactDOMServer.renderToString(<CustomSVG SvgFunction={Bullet} className={`stroke-gray-500 rect-stroke-gray-500 circle-stroke-gray-500`} height={20} width={20}/>),
        };

        return function ForwardedRQ({ forwardedRef, ...props }) {return <RQ ref={forwardedRef} {...props} />};
    },
    {ssr: false}
);

const format_types = {
    "simple": ['placeholder-token'],
    "basic" : ['bold', 'italic', 'underline', 'list'],
    "basic-with-header" : ['bold', 'italic', 'underline', 'list', 'header'],
}

const getToolbarTypes = (id, type) => {
    if(type === "simple") return [];
    else if(type === "basic") return  [
        <button key={`text-area-input-button-bold-${id}`} className="ql-bold"><CustomSVG SvgFunction={Bold} className={`stroke-gray-500 rect-stroke-gray-500 circle-stroke-gray-500`} height={20} width={20}/></button>,
        <button key={`text-area-input-button-italic-${id}`} className="ql-italic"><CustomSVG SvgFunction={Italic} className={`stroke-gray-500 rect-stroke-gray-500 circle-stroke-gray-500`} height={20} width={20}/></button>,
        <button key={`text-area-input-button-underline-${id}`} className="ql-underline"><CustomSVG SvgFunction={Underline} className={`stroke-gray-500 rect-stroke-gray-500 circle-stroke-gray-500`} height={20} width={20}/></button>,
        <span key={`text-area-input-button-formats-${id}`} className="ql-formats">
            <button key={`text-area-input-button-ordered-${id}`} className="ql-list" value="ordered"><CustomSVG SvgFunction={Ordered} className={`stroke-gray-500 rect-stroke-gray-500 circle-stroke-gray-500`} height={20} width={20}/></button>
            <button key={`text-area-input-button-bullet-${id}`} className="ql-list" value="bullet"><CustomSVG SvgFunction={Bullet} className={`stroke-gray-500 rect-stroke-gray-500 circle-stroke-gray-500`} height={20} width={20}/></button>
        </span>
    ];
    else if(type === "basic-with-header") return  [
        <select key={`text-area-input-header-select-${id}`} className="ql-header" defaultValue="">
            {/*<option value="1">H1</option>*/}
            <option value="">Normal</option>
            <option value="2">H2</option>
            <option value="3">H3</option>
            <option value="4">H4</option>
        </select>,
        <button key={`text-area-input-button-bold-${id}`} className="ql-bold"><CustomSVG SvgFunction={Bold} className={`stroke-gray-500 rect-stroke-gray-500 circle-stroke-gray-500`} height={20} width={20}/></button>,
        <button key={`text-area-input-button-italic-${id}`} className="ql-italic"><CustomSVG SvgFunction={Italic} className={`stroke-gray-500 rect-stroke-gray-500 circle-stroke-gray-500`} height={20} width={20}/></button>,
        <button key={`text-area-input-button-underline-${id}`} className="ql-underline"><CustomSVG SvgFunction={Underline} className={`stroke-gray-500 rect-stroke-gray-500 circle-stroke-gray-500`} height={20} width={20}/></button>,
        <span key={`text-area-input-button-formats-${id}`} className="ql-formats">
            <button key={`text-area-input-button-ordered-${id}`} className="ql-list" value="ordered"><CustomSVG SvgFunction={Ordered} className={`stroke-gray-500 rect-stroke-gray-500 circle-stroke-gray-500`} height={20} width={20}/></button>
            <button key={`text-area-input-button-bullet-${id}`} className="ql-list" value="bullet"><CustomSVG SvgFunction={Bullet} className={`stroke-gray-500 rect-stroke-gray-500 circle-stroke-gray-500`} height={20} width={20}/></button>
        </span>
    ];
    else return [];
}

const CustomToolbar = ({id, buttons, reverse}) => {
    return <div id={id} className={`d-flex flex-row align-items-center position-sticky`} style={{top: (reverse ? undefined : 0), bottom: (reverse ? 0 : undefined), zIndex: 10}}>
        {buttons.map((button, index) => <Fragment key={`${"toolbar-id"}-button-${index}`} >{button}</Fragment>)}
    </div>
};

function TextAreaInput ({id = "toolbar", label, label_color ="color-gray-700", label_font="text-sm-medium", required_mark, hint, error, type = "basic", inner_element = <></>, inner_element_under = <></>, custom_function = <></>, maxHeight = "unset", height = "unset", forwardedRef, customButtons, reverse, send_buttons, editorWrapperStyle, noNativeResize, ...props}) {
    var formats = format_types[type];
    var toolbar = getToolbarTypes(id, type);

    if(customButtons) toolbar = [...customButtons, ...toolbar]

    const modules = useMemo(() => ({
        toolbar: (type === "simple" ? "" : {container: `#${id}`})
    }), []);

    return <div className={`${classes["text-input"]} text-editor-input-container w-100 ${toolbar.length === 0 ? "no-toolbar" : ""} ${noNativeResize ? "no-resize" : ""}`}>
        {label && <div className={`d-flex flex-row text-nowrap gap-1 ${label_color} ${label_font}`}>
            {label}
            {required_mark && <div className="color-error-600">*</div>}
        </div>}
        <div className={`w-100 ${classes["main-container"]} d-flex flex-row align-items-end ${error ? classes["error"] : ""}`}>
            <div className="d-flex flex-column align-items-start flex-grow-1 mw-100" style={{gap: 6, minWidth: 0}}>
                {inner_element}
                <div className="d-flex flex-column align-self-stretch">
                    <div className={`d-flex flex-row align-items-end gap-2 w-100 ${height === "unset" ? "h-100" : ""}`} style={{maxHeight: maxHeight, height: height}}>
                        <div className="d-flex flex-column align-items-start flex-grow-1 h-100 mw-100 overflow-auto" style={{maxHeight: maxHeight, height: height}}>
                            {type !== "simple" && !reverse && <CustomToolbar id={id} buttons={toolbar} />}
                            <div className="w-100 h-100 overflow-auto" style={editorWrapperStyle}>
                                <ReactQuill forwardedRef={forwardedRef} formats={formats} theme="snow" modules={modules} {...props} />
                            </div>
                            {type !== "simple" && reverse && <CustomToolbar id={id} buttons={toolbar} reverse={true} />}
                        </div>
                        {custom_function}
                    </div>
                </div>
                {inner_element_under}
            </div>
            {send_buttons && <div className="flex-shrink-0">
                {send_buttons}
            </div>}
        </div>
        {hint && <div className={`${error ? "color-error-500" : "color-gray-500"} text-sm-regular`}>{hint}</div>}
    </div>
};
  
export default TextAreaInput;